<template>
   <div class="sn-tooltip-wrapper" ref="tooltipWrapperRef" data-test="tooltip-wrapper" :id="tooltipWrapperId">
      <!-- Trigger -->
      <div
         class="sn-tooltip-trigger"
         ref="tooltipTriggerRef"
         data-test="tooltip-trigger"
         :aria-describedby="props.disabled ? undefined : tooltipId"
         :aria-expanded="props.disabled ? undefined : isOpen"
         :aria-label="triggerAriaLabel"
         :tabIndex="tabIndex"
         @mouseenter="handleMouseEnter"
         @mouseleave="handleMouseLeave"
         @focusin="handleFocus"
         @focusout="handleBlur"
         @click="handleClick"
         @keydown.esc.capture="hideTooltip"
         @keydown.enter="handleEnterKey"
         @keydown.space="handleEnterKey"
         @keydown.tab="handleTabKey"
      >
         <slot>
            <button tabindex="-1">
               {{ triggerText }}
            </button>
         </slot>
      </div>
      <!-- Content -->
      <teleport to="body" :disabled="!teleport">
         <div
            :id="tooltipId"
            class="sn-tooltip-popper"
            ref="tooltipPopperRef"
            data-test="tooltip-popper"
            :class="placement"
            role="tooltip"
            popover="manual"
            :data-visible="isOpen"
            @click="handleTooltipClick"
            @mouseenter="handleTooltipMouseEnter"
            @mouseleave="handleTooltipMouseLeave"
            @keydown.esc="hideTooltip"
            @keydown.tab="handleTooltipTabKey"
         >
            <div class="sn-tooltip-arrow" ref="tooltipArrowRef" data-test="tooltip-arrow" />
            <slot name="content">
               <div class="sn-tooltip-content">
                  <SnIcon v-if="icon" :name="icon" size="small" data-test="tooltip-icon" />
                  {{ content }}
               </div>
            </slot>
         </div>
      </teleport>
   </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, useTemplateRef, watch } from "vue";
import SnIcon, { type Icon } from "../SnIcon/SnIcon.vue";
import { onClickOutside } from "@vueuse/core";
import { nanoid } from "nanoid";
import {
   computePosition,
   autoUpdate,
   flip,
   shift,
   offset,
   arrow,
   hide,
   type Placement,
   type Middleware,
   type Strategy,
} from "@floating-ui/dom";

interface Props {
   content?: string;
   icon?: Icon;
   triggerText?: string;
   trigger?: string[]; // can be 'click', 'hover', or 'focus'
   width?: string | number; // width of the tooltip
   closeOnClick?: boolean; // close tooltip when content is clicked
   placement?: Placement;
   delay?: number; // delay before showing tooltip (ms)
   distance?: number; // distance from trigger element (px)
   triggerAriaLabel?: string; // custom aria-label for tooltip
   strategy?: Strategy; // positioning strategy (absolute or fixed)
   teleport?: boolean; // teleport tooltip to body
   enableTooltipFocus?: boolean; // enables/disables tooltip focusing
   tabIndex?: 0 | -1;
   disabled?: boolean; // disables tooltip functionality while keeping slot content
}

const props = withDefaults(defineProps<Props>(), {
   triggerText: "Tooltip",
   trigger: () => ["hover", "focus"],
   closeOnClick: false,
   placement: "bottom",
   delay: 100,
   distance: 7,
   triggerAriaLabel: "",
   teleport: true,
   enableTooltipFocus: false,
   width: "auto",
   tabIndex: 0,
   disabled: false,
});

// Generate unique ID for ARIA attributes
const tooltipWrapperId = computed(() => `tooltip-wrapper-${nanoid(5)}`);
const tooltipId = computed(() => `tooltip-${nanoid(5)}`);

const wrapperRef = useTemplateRef("tooltipWrapperRef");
const triggerRef = useTemplateRef("tooltipTriggerRef");
const popperRef = useTemplateRef("tooltipPopperRef");
const arrowRef = useTemplateRef("tooltipArrowRef");

// Refs
const isOpen = ref(false);
const hoverTimeout = ref<number | null>(null);
const cleanupAutoUpdate = ref<(() => void) | null>(null);
const isTooltipHovered = ref(false); // Track if tooltip itself is being hovered

// Methods
const showTooltip = () => {
   if (props.disabled) return;

   if (hoverTimeout.value) {
      clearTimeout(hoverTimeout.value);
      hoverTimeout.value = null;
   }

   hoverTimeout.value = window.setTimeout(() => {
      isOpen.value = true;
      popperRef.value?.showPopover();
      nextTick(() => {
         updatePosition();
         setupAutoUpdate();
      });
   }, props.delay);
};

const hideTooltip = () => {
   // Don't hide tooltip if it's currently being hovered
   if (!props.closeOnClick && isTooltipHovered.value && props.trigger.includes("hover")) {
      return;
   }

   if (hoverTimeout.value) {
      clearTimeout(hoverTimeout.value);
      hoverTimeout.value = null;
   }

   isOpen.value = false;
   popperRef.value?.hidePopover();

   // Clean up autoUpdate when tooltip is hidden
   if (cleanupAutoUpdate.value) {
      cleanupAutoUpdate.value();
      cleanupAutoUpdate.value = null;
   }
};

const handleMouseEnter = () => {
   if (props.trigger.includes("hover") && !props.disabled) {
      showTooltip();
   }
};

const handleMouseLeave = () => {
   if (props.trigger.includes("hover")) {
      // Add a small delay before hiding to allow moving mouse to tooltip
      hoverTimeout.value = window.setTimeout(() => {
         if (!isTooltipHovered.value) {
            hideTooltip();
         }
      }, 300);
   }
};

// Track when mouse enters the tooltip itself
const handleTooltipMouseEnter = () => {
   isTooltipHovered.value = true;
   if (hoverTimeout.value) {
      clearTimeout(hoverTimeout.value);
      hoverTimeout.value = null;
   }
};

// Track when mouse leaves the tooltip itself
const handleTooltipMouseLeave = () => {
   isTooltipHovered.value = false;
   if (props.trigger.includes("hover")) {
      hoverTimeout.value = window.setTimeout(() => {
         if (!isTooltipHovered.value) {
            hideTooltip();
         }
      }, 50);
   }
};

// Track if we've just tabbed back from tooltip
const isTabbingFromTooltip = ref(false);

const handleFocus = () => {
   if (props.trigger.includes("focus") && !isTabbingFromTooltip.value && !props.disabled) {
      showTooltip();
   }
   // Reset tabbing flag after a short delay
   setTimeout(() => {
      isTabbingFromTooltip.value = false;
   }, 100);
};

const handleBlur = (event: FocusEvent) => {
   // Only hide on blur if the focus is not moving to the tooltip itself
   if (props.trigger.includes("focus")) {
      // Check if the new focus target is within our tooltip
      const tooltip = popperRef.value;
      if (tooltip && event.relatedTarget instanceof Element && tooltip.contains(event.relatedTarget)) {
         // Focus moved to the tooltip, don't hide
         return;
      }
      hideTooltip();
   }
};

const handleClick = () => {
   if (props.trigger.includes("click") && !props.disabled) {
      isOpen.value = !isOpen.value;
      if (isOpen.value) {
         popperRef.value?.showPopover();
         nextTick(() => {
            updatePosition();
            setupAutoUpdate();
         });
      } else if (cleanupAutoUpdate.value) {
         popperRef.value?.hidePopover();
         cleanupAutoUpdate.value();
         cleanupAutoUpdate.value = null;
      }
   }
};

const handleEnterKey = () => {
   // Toggle tooltip visibility for both Enter and Space keys
   if (props.trigger.includes("click") && !props.disabled) {
      isOpen.value = !isOpen.value;

      if (isOpen.value) {
         popperRef.value?.showPopover();
         nextTick(() => {
            updatePosition();
            setupAutoUpdate();
         });
      } else if (cleanupAutoUpdate.value) {
         popperRef.value?.hidePopover();
         cleanupAutoUpdate.value();
         cleanupAutoUpdate.value = null;
      }
   }
};

// Handle Tab key from trigger
const handleTabKey = (event: KeyboardEvent) => {
   // We won't prevent the default tab behavior, allowing natural tabbing
   // Only if enableTooltipFocus is true, we'll move focus to tooltip
   if (props.enableTooltipFocus && isOpen.value && !event.shiftKey && popperRef.value) {
      event.preventDefault();
      popperRef.value.focus();
   } else if (isOpen.value) {
      hideTooltip();
   }
};

// Handle Tab key from tooltip
const handleTooltipTabKey = (event: KeyboardEvent) => {
   // If we're tabbing backward (shift+tab), we'll go back to the trigger
   if (event.shiftKey && triggerRef.value) {
      // Set tabbing flag to prevent re-triggering the tooltip when tabbing backward from tooltip
      isTabbingFromTooltip.value = true;

      event.preventDefault();
      const firstFocusableElement = triggerRef.value.querySelector(
         'button, a, input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (firstFocusableElement) {
         (firstFocusableElement as HTMLElement).focus();
      } else {
         triggerRef.value.focus();
      }
   }
};

const handleTooltipClick = (event: MouseEvent) => {
   // Stop propagation in all cases to prevent triggering document clicks
   event.stopPropagation();

   if (props.closeOnClick) {
      hideTooltip();
   }
};

// Handle tab-away from tooltip content
const handleTooltipFocusOut = (event: FocusEvent) => {
   // Check if focus is moving outside the tooltip
   if (!popperRef.value?.contains(event.relatedTarget as Node)) {
      hideTooltip();
   }
};

// Set up automatic update of position
const setupAutoUpdate = () => {
   if (!triggerRef.value || !popperRef.value || cleanupAutoUpdate.value) return;

   cleanupAutoUpdate.value = autoUpdate(triggerRef.value, popperRef.value, updatePosition);

   // We remove the auto-focus behavior
   // Only focus the tooltip if enableTooltipFocus is true
   if (props.enableTooltipFocus) {
      nextTick(() => {
         if (popperRef.value && popperRef.value.getAttribute("tabindex") === "0") {
            popperRef.value.focus();
         }
      });
   }
};

// Position the tooltip using Floating UI
const updatePosition = async () => {
   if (!triggerRef.value || !popperRef.value) return;

   const middlewares: Middleware[] = [];

   // Add offset middleware
   middlewares.push(offset(props.distance));

   // Add shift & flip middleware
   middlewares.push(flip());
   middlewares.push(shift({ padding: 8 }));

   // // Hide the tooltip if the trigger is no longer visible
   middlewares.push(hide());

   // Add arrow middleware - we'll use this for initial positioning
   if (arrowRef.value) {
      middlewares.push(
         arrow({
            element: arrowRef.value,
            padding: 12, // Add padding to prevent arrow from getting too close to the edges
         })
      );
   }

   // Compute the position
   const { x, y, placement, middlewareData } = await computePosition(triggerRef.value, popperRef.value, {
      placement: props.placement, // Use placement with alignment
      strategy: props.strategy,
      middleware: middlewares,
   });

   // Apply the position and width to the tooltip
   Object.assign(popperRef.value.style, {
      left: `${x}px`,
      top: `${y}px`,
      maxWidth: props.width === "auto" ? "auto" : typeof props.width === "number" ? `${props.width}px` : props.width,
   });

   // Set the data attribute for CSS styling
   popperRef.value.setAttribute("data-placement", placement);

   // Position the arrow if it exists
   if (arrowRef.value && middlewareData.arrow) {
      const { x: arrowX, y: arrowY } = middlewareData.arrow;

      const staticSide = {
         top: "bottom",
         bottom: "top",
         left: "right",
         right: "left",
      }[placement.split("-")[0]];

      if (staticSide) {
         // Use the provided arrow coordinates from the middleware
         Object.assign(arrowRef.value.style, {
            left: arrowX != null ? `${arrowX}px` : "",
            top: arrowY != null ? `${arrowY}px` : "",
            right: "",
            bottom: "",
            [staticSide]: "-4px", // Arrow size
         });
      }
   }

   // Hide tooltip if trigger is no longer visible
   if (middlewareData.hide?.referenceHidden) {
      popperRef.value.style.visibility = "hidden";
   } else {
      popperRef.value.style.visibility = "visible";
   }
};

// Set up event handlers
onMounted(() => {
   // Setup event listeners on tooltip when it gets created
   nextTick(() => {
      if (popperRef.value) {
         // Make tooltip content focusable only if enableTooltipFocus is true
         popperRef.value.setAttribute("tabindex", props.enableTooltipFocus ? "0" : "-1");

         // Add focusout event listener to the tooltip content
         popperRef.value.addEventListener("focusout", handleTooltipFocusOut);

         // Add keydown event for shift+tab to handle backward tabbing
         popperRef.value.addEventListener("keydown", (e: KeyboardEvent) => {
            if (e.key === "Tab" && e.shiftKey) {
               handleTooltipTabKey(e);
            }
         });
      }
   });

   // Set up click outside detection
   if (props.trigger.includes("click")) {
      onClickOutside(
         wrapperRef,
         () => {
            if (isOpen.value) {
               hideTooltip();
            }
         },
         { ignore: [popperRef] }
      );
   }
});

// Clean up resources
onUnmounted(() => {
   if (hoverTimeout.value) {
      clearTimeout(hoverTimeout.value);
   }

   if (cleanupAutoUpdate.value) {
      cleanupAutoUpdate.value();
   }

   // Remove tooltip focusout listener
   if (popperRef.value) {
      popperRef.value.removeEventListener("focusout", handleTooltipFocusOut);
      popperRef.value.removeEventListener("keydown", (e: KeyboardEvent) => {
         if (e.key === "Tab" && e.shiftKey) {
            handleTooltipTabKey(e);
         }
      });
   }
});

// Watch for changes to placement and update position
watch(
   () => props.placement,
   () => {
      if (isOpen.value) {
         nextTick(() => {
            updatePosition();
         });
      }
   }
);
</script>

<style lang="scss" scoped>
@forward "./SnTooltip";

.sn-tooltip-wrapper {
   position: relative;
   display: inline-flex;
   align-items: center;

   .sn-tooltip-trigger {
      display: inline-block;
   }
}

.sn-tooltip-popper {
   position: absolute;
   top: 0;
   left: 0;
   z-index: 10000;
   width: max-content; /* Will be overridden by inline style if width prop is set */
   padding: var(--popper-padding);
   margin: 0;
   overflow: visible;
   font-size: var(--popper-font-size);
   color: var(--popper-text-color);
   word-wrap: break-word;
   background: var(--popper-background);
   border-radius: var(--popper-border-radius);
   box-shadow: var(--popper-box-shadow);

   // Arrow appearance
   .sn-tooltip-arrow {
      position: absolute;
      width: 8px;
      height: 8px;
      pointer-events: none;
      background: var(--popper-background);
      transform: rotate(45deg);
   }

   .sn-tooltip-content {
      position: relative;
      display: flex;
      gap: var(--size-base-6);
      align-items: center;
      line-height: 1.32;
      text-align: left;
      text-transform: none;
   }
}

// Arrow placement
.sn-tooltip-popper[data-placement^="top"] .sn-tooltip-arrow {
   bottom: -4px;
}

.sn-tooltip-popper[data-placement^="bottom"] .sn-tooltip-arrow {
   top: -4px;
}

.sn-tooltip-popper[data-placement^="left"] .sn-tooltip-arrow {
   right: -4px;
}

.sn-tooltip-popper[data-placement^="right"] .sn-tooltip-arrow {
   left: -4px;
}
</style>
